import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

type ThemeContextValue = {
  darkMode: boolean;
  toggleDarkMode: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = "theme";

const getStoredTheme = () => {
  if (typeof window === "undefined") return undefined;
  return window.localStorage.getItem(THEME_STORAGE_KEY) as "dark" | "light" | null;
};

const getSystemPreference = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

export function ThemeProvider({ children }: PropsWithChildren) {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const stored = getStoredTheme();

    if (stored) {
      setDarkMode(stored === "dark");
      document.documentElement.classList.toggle("dark", stored === "dark");
      return;
    }

    const prefersDark = getSystemPreference();
    setDarkMode(prefersDark);
    document.documentElement.classList.toggle("dark", prefersDark);
    window.localStorage.setItem(THEME_STORAGE_KEY, prefersDark ? "dark" : "light");
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const next = !prev;
      const value = next ? "dark" : "light";

      if (typeof window !== "undefined") {
        window.localStorage.setItem(THEME_STORAGE_KEY, value);
      }

      document.documentElement.classList.toggle("dark", next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      darkMode,
      toggleDarkMode,
    }),
    [darkMode, toggleDarkMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used inside ThemeProvider");
  }
  return context;
}
